import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type BadgeTono = "neutro" | "primario" | "exito" | "alerta" | "peligro";

const TONOS: Record<BadgeTono, string> = {
  neutro: "bg-superficie-2 text-texto-suave",
  primario: "bg-primario-suave text-primario",
  exito: "bg-exito-suave text-exito",
  alerta: "bg-alerta-suave text-alerta",
  peligro: "bg-peligro-suave text-peligro",
};

// Como el estado de las tarjetas de la grilla de Proyecto: mayúsculas chicas y espaciadas.
const CARPETA = "px-2.5 text-[0.6875rem] uppercase tracking-[0.14em]";

export interface BadgeProps {
  tono?: BadgeTono;
  className?: string;
  children: ReactNode;
  /** Si está, muestra una × al final que la llama (ej. quitar una etiqueta). */
  onQuitar?: () => void;
  /** Nombre accesible de la × (por defecto "Quitar"). Ej.: "Quitar etiqueta Pintura". */
  etiquetaQuitar?: string;
  /** Si está (y no hay `onQuitar`), la Badge entera es un botón que la llama (ej. filtrar o agregar una etiqueta). */
  onTocar?: () => void;
  /** Nombre accesible del botón (por defecto, el texto). Ej.: "Ver todos los de Pintura". */
  etiquetaTocar?: string;
}

export function Badge({ tono = "neutro", className, children, onQuitar, etiquetaQuitar = "Quitar", onTocar, etiquetaTocar }: BadgeProps) {
  if (onTocar && !onQuitar) {
    return (
      <button
        type="button"
        aria-label={etiquetaTocar}
        onClick={onTocar}
        className={cn(
          "inline-flex min-h-7 items-center rounded-full px-2.5 py-0.5 text-chico font-medium hover:ring-1 hover:ring-borde-fuerte",
          CARPETA,
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primario/40",
          TONOS[tono],
          className,
        )}
      >
        {children}
      </button>
    );
  }
  return (
    <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-chico font-medium", CARPETA, onQuitar && "pr-1", TONOS[tono], className)}>
      {children}
      {onQuitar && (
        <button
          type="button"
          aria-label={etiquetaQuitar}
          onClick={onQuitar}
          className="ml-1 grid size-5 place-items-center rounded-full hover:bg-borde focus:outline-none focus-visible:ring-2 focus-visible:ring-primario/40"
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </span>
  );
}
